import type { Door } from '@/content/schema';
import type { FloorRuntime } from './floorBuilder';

/**
 * 網格 A*（0.5m 一格）。
 * - 駐廠品保巡邏
 * - 玩家點地板／點機台時自動找路，而且「只走合規的衛生動線」（不會自動帶你逆行）
 */
export class NavGrid {
  readonly cell = 0.5; readonly cols: number; readonly rows: number; private open: Uint8Array; private zone: Int16Array;
  private allowed = new Set<string>();
  constructor(private rt: FloorRuntime, doorOk: (d: Door) => boolean = () => true) {
    const [W, D] = rt.floor.size;
    this.cols = Math.round(W / this.cell); this.rows = Math.round(D / this.cell);
    this.open = new Uint8Array(this.cols * this.rows);
    this.zone = new Int16Array(this.cols * this.rows).fill(-1);
    const zones = rt.floor.zones;
    for (let r = 0; r < this.rows; r++) for (let c = 0; c < this.cols; c++) {
      const [x, z] = this.toWorld(c, r);
      this.open[r * this.cols + c] = rt.walkable(x, z, 0.36, true) ? 1 : 0;   // 手動門（冷凍庫、碼頭）規劃路線時當作可以推開
      const zn = rt.zoneAt(x, z); this.zone[r * this.cols + c] = zn ? zones.indexOf(zn) : -1;
    }
    // 允許的區域轉換：同等級、門的正方向、開放門雙向
    // 同等級但用牆隔開的區域只能經過門；有門的才加入（open／locked／hidden 雙向）
    zones.forEach((a, i) => zones.forEach((b, j) => {
      if (i === j || a.level !== b.level) return;
      const viaDoor = [...rt.doors.values()].some(({ data: d }) => (d.from === a.id && d.to === b.id) || (d.from === b.id && d.to === a.id));
      if (!viaDoor) this.allowed.add(`${i}>${j}`);   // 沒有門分隔（例如同一個大空間裡的兩塊）
    }));
    for (const { data: d } of rt.doors.values()) {
      if (!doorOk(d)) continue;
      const i = zones.findIndex(z => z.id === d.from), j = zones.findIndex(z => z.id === d.to);
      this.allowed.add(`${i}>${j}`); if (d.type === 'open' || d.type === 'locked' || d.type === 'hidden' || d.type === 'airshower') this.allowed.add(`${j}>${i}`);
    }
  }
  private canStep(a: number, b: number, hygiene: boolean) {
    if (!hygiene) return true;
    const za = this.zone[a], zb = this.zone[b];
    return za === zb || za < 0 || zb < 0 || this.allowed.has(`${za}>${zb}`);
  }
  nearestOpen(x: number, z: number): [number, number] | null {
    const [c0, r0] = this.toCell(x, z);
    for (let rad = 0; rad < 6; rad++) for (let dr = -rad; dr <= rad; dr++) for (let dc = -rad; dc <= rad; dc++) {
      if (Math.max(Math.abs(dr), Math.abs(dc)) !== rad) continue;
      if (this.isOpen(c0 + dc, r0 + dr)) return this.toWorld(c0 + dc, r0 + dr);
    }
    return null;
  }
  toWorld(c: number, r: number): [number, number] { const [W, D] = this.rt.floor.size; return [-W / 2 + (c + 0.5) * this.cell, -D / 2 + (r + 0.5) * this.cell]; }
  toCell(x: number, z: number): [number, number] { const [W, D] = this.rt.floor.size; return [Math.floor((x + W / 2) / this.cell), Math.floor((z + D / 2) / this.cell)]; }
  isOpen(c: number, r: number) { return c >= 0 && r >= 0 && c < this.cols && r < this.rows && this.open[r * this.cols + c] === 1; }
  randomOpenIn(rect: [number, number, number, number], rnd: () => number): [number, number] | null {
    for (let i = 0; i < 40; i++) {
      const x = rect[0] + 0.8 + rnd() * (rect[2] - 1.6), z = rect[1] + 0.8 + rnd() * (rect[3] - 1.6);
      const [c, r] = this.toCell(x, z); if (this.isOpen(c, r)) return this.toWorld(c, r);
    }
    return null;
  }
  find(from: [number, number], to: [number, number], hygiene = false, avoid: [number, number][] = []): [number, number][] | null {
    const [sc, sr] = this.toCell(...from), [tc, tr] = this.toCell(...to);
    if (!this.isOpen(tc, tr)) return null;
    const N = this.cols * this.rows, g = new Float32Array(N).fill(Infinity), came = new Int32Array(N).fill(-1);
    const startI = sr * this.cols + sc, goal = tr * this.cols + tc;
    const h = (i: number) => Math.hypot((i % this.cols) - tc, Math.floor(i / this.cols) - tr);
    // 二元堆積（大地圖 1F 有 8000 格，線性掃描太慢）
    const heap: [number, number][] = [];
    const push = (f: number, i: number) => { heap.push([f, i]); let k = heap.length - 1; while (k > 0) { const p = (k - 1) >> 1; if (heap[p][0] <= heap[k][0]) break; [heap[p], heap[k]] = [heap[k], heap[p]]; k = p; } };
    const pop = () => { const top = heap[0]; const last = heap.pop()!; if (heap.length) { heap[0] = last; let k = 0; for (;;) { const l = 2 * k + 1, r = l + 1; let m = k; if (l < heap.length && heap[l][0] < heap[m][0]) m = l; if (r < heap.length && heap[r][0] < heap[m][0]) m = r; if (m === k) break; [heap[m], heap[k]] = [heap[k], heap[m]]; k = m; } } return top; };
    g[startI] = 0; push(h(startI), startI);
    const closed = new Uint8Array(N);
    while (heap.length) {
      const [, cur] = pop();
      if (closed[cur]) continue;
      if (cur === goal) break;
      closed[cur] = 1;
      const cc = cur % this.cols, cr = Math.floor(cur / this.cols);
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
        const nc = cc + dc, nr = cr + dr; if (!this.isOpen(nc, nr)) continue;
        if (dc && dr && (!this.isOpen(cc + dc, cr) || !this.isOpen(cc, cr + dr))) continue;
        const ni = nr * this.cols + nc; if (closed[ni] || !this.canStep(cur, ni, hygiene)) continue;
        if (avoid.length) { const [wx, wz] = this.toWorld(nc, nr); if (avoid.some(([ax, az]) => Math.hypot(ax - wx, az - wz) < 0.8)) continue; }
        const ng = g[cur] + (dc && dr ? 1.414 : 1);
        if (ng < g[ni]) { g[ni] = ng; came[ni] = cur; push(ng + h(ni), ni); }
      }
    }
    if (came[goal] === -1 && goal !== startI) return null;
    const path: [number, number][] = []; let i = goal;
    while (i !== -1 && i !== startI) { path.push(this.toWorld(i % this.cols, Math.floor(i / this.cols))); i = came[i]; }
    return path.reverse();
  }
}
